import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { InfoBox, LoadingState, cn } from '@biznomi/ui-react'
import { manualReasonLabel } from '@biznomi/types'
import { getTodaySummary } from '@/lib/api/admin'

/**
 * 오늘 현황 — 전부 **사실 집계**다.
 *
 * "결근 N명" 같은 판정 문구를 쓰지 않는다. 아직 안 찍은 것과 결근은 다르고,
 * 그 둘을 시스템이 구분하려면 날짜 순회 판정이 필요하다 (= 폐기한 cron).
 * 여기서는 "기록 없음" 이라고만 말한다. 사유는 사람이 안다.
 */

function Stat({
  label,
  value,
  hint,
  tone = 'default',
  onClick,
}: {
  label: string
  value: number
  hint?: string
  tone?: 'default' | 'warn'
  onClick?: () => void
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      className={cn(
        'rounded-card bg-card p-4 text-left',
        onClick && 'transition-shadow hover:shadow-md',
      )}
    >
      <p className="text-sm text-slate-500">{label}</p>
      <p
        className={cn(
          'mt-1 text-2xl font-bold tabular-nums',
          tone === 'warn' && value > 0 && 'text-[var(--color-warning)]',
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </Tag>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'today-summary'],
    queryFn: async () => {
      const res = await getTodaySummary()
      if (!res.success || !res.data) throw new Error(res.error ?? '현황을 불러오지 못했습니다')
      return res.data
    },
    refetchInterval: 60_000,
  })

  if (isLoading) return <LoadingState />
  if (error) return <InfoBox tone="danger">{(error as Error).message}</InfoBox>
  if (!data) return null

  // enum 기반 표시는 하드코딩하지 않는다 — 서버가 준 것만 그린다
  const absences = Object.entries(data.absenceBreakdown ?? {}).filter(([, n]) => n > 0)

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-bold">오늘 현황</h1>
        <p className="mt-1 text-sm text-slate-500">전체 {data.totalEmployees}명</p>
      </header>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="출근" value={data.presentEmployees} hint="출근을 찍은 사람" />
        <Stat label="근무 중" value={data.workingEmployees} hint="퇴근 전" />
        <Stat label="퇴근" value={data.checkoutEmployees} hint="퇴근까지 찍음" />
        <Stat
          label="기록 없음"
          value={data.absentEmployees}
          hint="눌러서 명단 보기"
          onClick={() => navigate('/admin/untagged')}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Stat
          label="승인 대기"
          value={data.pendingManualRecords}
          hint={`${data.pendingManualEmployees}명 · 눌러서 처리`}
          tone="warn"
          onClick={() => navigate('/admin/approvals')}
        />
        <Stat label="부재" value={data.absenceTotal} hint="휴가·출장·교육 등" />
      </div>

      {absences.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-bold text-slate-500">부재 내역</h2>
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-card bg-card">
            {absences.map(([reason, count]) => (
              <li key={reason} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm">{manualReasonLabel(reason)}</span>
                <span className="text-sm font-semibold tabular-nums">{count}명</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="text-xs leading-relaxed text-slate-400">
        위 숫자는 태깅 기록을 센 것입니다. 시스템이 지각·결근을 판단하지는 않습니다.
      </p>
    </div>
  )
}
